// Global Instructions Rule Applied!
//
// The distribution form's Allocation grid (waterfall phase 6 PR 2b, owner
// 2026-10-08): the calculation shown the way the waterfall works. ONE ROW PER
// INVESTOR, the tiers left to right - each a bucket that fills before the
// next one takes anything - then what the investor is paid: the cash to the
// partner and the carry its profit bore, each with one override.
//
//   Investor · Share · Taken first · [Tier n: Partner / Carry] ... ·
//   To partner: Calculated / Correction / Override / Effective ·
//   Carry:      Calculated / Correction / Override / Effective
//
// * A tier's group shows only the classes the tier pays (the return of
//   capital pays the partner only, the catch-up the carry only). A cell is
//   the effective amount under the tier, summed over the sources; under a
//   partner cell the bucket after this distribution (Full / n to go / Not
//   reached; nothing on a tier without a stop), on a carry cell the rate.
// * A user may override what is distributed, never the waterfall: there is
//   one Override per investor and class, not one per tier. The server
//   attributes it to the tier rows (wf__attribute_partner_override) - so the
//   tier cells show the SAVED attribution and move on Save, not while typing.
// * The Calculated / Override pair follows the house grammar
//   (docs/FRONTEND_DESIGN_PATTERNS.md "Calculated and Override in a grid"):
//   Calculated struck through when overridden, the input in status='warning',
//   clearing it is the undo, Correction read-only between the pair with what
//   is carried to a later distribution under it. The total row follows the
//   row rule.
// * The GP commitment's own share is paid to it as such: no tier, a cash
//   override only.
// * A row expands into the investor's tier test (PartnerTierDetail).
//
// The pivot is the server's (wf__distribution_partners): nothing here adds up
// per-commitment rows. The priority lines ahead of the tiers are edited in
// the Taken first panel and only totalled here.

import React, { useMemo } from 'react'
import { InputNumber, Table, Tag, Tooltip, Typography } from 'antd'
import type { ColumnsType, ColumnType } from 'antd/es/table'
import NumberText from '../../../../core/components/NumberText.tsx'
import type { useNumberFormat } from '../../../../core/utils/inputNumberFormat.ts'
import type {
  DistributionPartner,
  DistributionPartnerLine,
  DistributionTierCell,
  DistributionTierStrip
} from '../../../../state_manager/data_models/distribution_detail.ts'
import PartnerTierDetail from './PartnerTierDetail.tsx'
import {
  CALCULATED_TITLE,
  CORRECTION_HELP,
  CORRECTION_TITLE,
  EFFECTIVE_TITLE,
  OVERRIDE_TITLE,
  fmtRate,
  hasFigure,
  tierStateTag,
  tierTitle
} from './allocationLabels.ts'

const { Text } = Typography

/** The two lines an investor is paid on: its cash, and the carry its profit bore. */
export type PartnerLineKey = 'to_partner' | 'carry'
/** The override as it stands for a line: this session's edit, else the saved one. */
export type PartnerOverrideReader = (partner: DistributionPartner, line: PartnerLineKey) => number | null
export type PartnerOverrideWriter = (partner: DistributionPartner, line: PartnerLineKey, value: number | null) => void

interface DistributionAllocationTableProps {
  partners: DistributionPartner[]
  tiers: DistributionTierStrip[]
  /** how earlier distributions were checked (cfg.waterfall_history_checks.code) */
  historyCheck: string | null | undefined
  dealByDeal: boolean
  readOnly: boolean
  /** the Override inputs render disabled with this reason (a consolidated distribution: none in this phase) */
  overridesLockedReason?: string | null
  currencyFmt: ReturnType<typeof useNumberFormat>
  override: PartnerOverrideReader
  setOverride: PartnerOverrideWriter
  /** what is taken first per commitment as it stands (the panel's unsaved edits included) */
  takenFirst?: Record<string, number>
}

const LINE_TITLES: Record<PartnerLineKey, string> = { to_partner: 'To partner', carry: 'Carry' }
const LINE_ARIA: Record<PartnerLineKey, string> = { to_partner: 'Override to partner for', carry: 'Override carry for' }

/** The amount Issue books for a line: the override if there is one, else what was calculated. */
export function partnerLineEffective(line: DistributionPartnerLine | null | undefined, override: number | null): number {
  if (!line) return 0
  return override ?? line.calculated
}

/** What an investor's two lines pay as they stand (the priority lines are not part of it). */
export function partnerLinesEffective(partner: DistributionPartner, read: PartnerOverrideReader): number {
  return (
    partnerLineEffective(partner.to_partner, partner.to_partner ? read(partner, 'to_partner') : null) +
    partnerLineEffective(partner.carry, partner.carry ? read(partner, 'carry') : null)
  )
}

const dash = <Text type='secondary'>—</Text>
const struck = (f: React.ReactNode) => (
  <Text delete type='secondary'>
    {f}
  </Text>
)
const strong = (f: React.ReactNode) => <Text strong>{f}</Text>

export default function DistributionAllocationTable({
  partners,
  tiers,
  historyCheck,
  dealByDeal,
  readOnly,
  overridesLockedReason,
  currencyFmt,
  override,
  setOverride,
  takenFirst
}: DistributionAllocationTableProps) {
  const sortedTiers = useMemo(() => [...tiers].sort((a, b) => a.tier_sequence - b.tier_sequence), [tiers])
  // the leaf columns of the tier groups, in order: a tier's partner cell, then its carry cell
  const tierLeaves = useMemo(
    () =>
      sortedTiers.flatMap((t) => [
        ...(t.prog__pays_lp ? [{ tier: t, kind: 'partner' as const }] : []),
        ...(t.prog__pays_gp ? [{ tier: t, kind: 'carry' as const }] : [])
      ]),
    [sortedTiers]
  )
  const takenFirstOf = (p: DistributionPartner): number => takenFirst?.[p.fund_commitment_id] ?? p.prog__taken_first
  const cellOf = (p: DistributionPartner, sequence: number): DistributionTierCell | undefined =>
    p.tiers.find((c) => c.tier_sequence === sequence)
  // the GP commitment's own share runs through no tier: one cell says so across the tier columns
  const paidAsSuch = (p: DistributionPartner): boolean => p.fund_commitments__is_gp_commitment && p.tests.length === 0

  const totals = useMemo(() => {
    const line = (key: PartnerLineKey) => {
      let calculated = 0
      let correction = 0
      let effective = 0
      let overridden = false
      for (const p of partners) {
        const l = p[key]
        if (!l) continue
        const ov = override(p, key)
        calculated += l.calculated
        correction += l.correction
        effective += partnerLineEffective(l, ov)
        if (ov != null) overridden = true
      }
      return { calculated, correction, effective, overridden }
    }
    return {
      share: partners.reduce((acc, p) => acc + p.prog__share, 0),
      takenFirst: partners.reduce((acc, p) => acc + (takenFirst?.[p.fund_commitment_id] ?? p.prog__taken_first), 0),
      leaves: tierLeaves.map(({ tier, kind }) =>
        partners.reduce((acc, p) => {
          const cell = p.tiers.find((c) => c.tier_sequence === tier.tier_sequence)
          return acc + (cell ? (kind === 'partner' ? cell.partner_effective : cell.carry_effective) : 0)
        }, 0)
      ),
      to_partner: line('to_partner'),
      carry: line('carry')
    }
  }, [partners, tierLeaves, override, takenFirst])

  const tierColumns: ColumnsType<DistributionPartner> = sortedTiers
    .filter((t) => t.prog__pays_lp || t.prog__pays_gp)
    .map((t) => ({
      title: tierTitle(t.tier_sequence, t.tier_type_label),
      key: `tier-${t.tier_sequence}`,
      children: [
        ...(t.prog__pays_lp
          ? [
              {
                title: 'Partner',
                key: `tier-${t.tier_sequence}-partner`,
                width: 150,
                align: 'right' as const,
                render: (_: unknown, p: DistributionPartner) => {
                  const cell = cellOf(p, t.tier_sequence)
                  if (!cell) return dash
                  const tag = tierStateTag(cell.state_after)
                  return (
                    <div>
                      <NumberText value={cell.partner_effective} />
                      {tag ? (
                        <div>
                          <Text type='secondary' className='text-xs'>
                            {cell.state_after === 'open' && hasFigure(cell.remaining) ? (
                              <>
                                <NumberText value={cell.remaining} /> {tag.label}
                              </>
                            ) : cell.state_after === 'open' ? (
                              'Open'
                            ) : (
                              tag.label
                            )}
                          </Text>
                        </div>
                      ) : null}
                    </div>
                  )
                }
              }
            ]
          : []),
        ...(t.prog__pays_gp
          ? [
              {
                title: 'Carry',
                key: `tier-${t.tier_sequence}-carry`,
                width: 140,
                align: 'right' as const,
                render: (_: unknown, p: DistributionPartner) => {
                  const cell = cellOf(p, t.tier_sequence)
                  if (!cell) return dash
                  return (
                    <div>
                      <NumberText value={cell.carry_effective} />
                      {cell.rate_gp != null ? (
                        <div>
                          <Text type='secondary' className='text-xs'>
                            at {fmtRate(cell.rate_gp)}
                          </Text>
                        </div>
                      ) : null}
                    </div>
                  )
                }
              }
            ]
          : [])
      ]
    }))

  // the GP commitment's row: the first tier leaf spans every tier column
  const leafColumns = tierColumns.flatMap((group) => ('children' in group ? (group.children as ColumnType<DistributionPartner>[]) : []))
  leafColumns.forEach((leaf, index) => {
    const render = leaf.render
    leaf.onCell = (p: DistributionPartner) => (paidAsSuch(p) ? { colSpan: index === 0 ? leafColumns.length : 0 } : {})
    leaf.render = (value: unknown, p: DistributionPartner, i: number) =>
      paidAsSuch(p) ? (
        <div className='text-center'>
          <Text type='secondary'>paid as such</Text>
        </div>
      ) : render ? (
        render(value, p, i)
      ) : null
  })

  const lineColumns = (key: PartnerLineKey): ColumnsType<DistributionPartner>[number] => ({
    title: LINE_TITLES[key],
    key,
    children: [
      {
        title: CALCULATED_TITLE,
        key: `${key}-calculated`,
        width: 150,
        align: 'right',
        render: (_: unknown, p) => {
          const line = p[key]
          if (!line) return dash
          return override(p, key) != null ? <NumberText value={line.calculated} wrap={struck} /> : <NumberText value={line.calculated} />
        }
      },
      {
        title: (
          <Tooltip title={CORRECTION_HELP}>
            <span>{CORRECTION_TITLE}</span>
          </Tooltip>
        ),
        key: `${key}-correction`,
        width: 150,
        align: 'right',
        render: (_: unknown, p) => {
          const line = p[key]
          if (!line || (!hasFigure(line.correction) && !hasFigure(line.carried))) return null
          return (
            <div>
              {hasFigure(line.correction) ? <NumberText value={line.correction} /> : null}
              {hasFigure(line.carried) ? (
                <div>
                  <Text type='secondary'>
                    <NumberText value={line.carried} /> carried
                  </Text>
                </div>
              ) : null}
            </div>
          )
        }
      },
      {
        title: OVERRIDE_TITLE,
        key: `${key}-override`,
        width: 160,
        align: 'right',
        render: (_: unknown, p) => {
          const line = p[key]
          if (!line) return null
          const ov = override(p, key)
          if (readOnly) return ov != null ? <NumberText value={ov} /> : null
          return (
            <InputNumber
              size='small'
              className='w-full'
              aria-label={`${LINE_ARIA[key]} ${p.investors__name ?? 'this investor'}`}
              value={ov}
              placeholder='-'
              disabled={overridesLockedReason != null}
              title={overridesLockedReason ?? undefined}
              status={ov != null ? 'warning' : undefined}
              formatter={currencyFmt.formatter}
              parser={currencyFmt.parser}
              onChange={(v) => setOverride(p, key, v == null ? null : Number(v))}
            />
          )
        }
      },
      {
        title: EFFECTIVE_TITLE,
        key: `${key}-effective`,
        width: 150,
        align: 'right',
        render: (_: unknown, p) => {
          const line = p[key]
          if (!line) return dash
          return <NumberText value={partnerLineEffective(line, override(p, key))} wrap={strong} />
        }
      }
    ]
  })

  const columns: ColumnsType<DistributionPartner> = [
    {
      title: 'Investor',
      dataIndex: 'investors__name',
      fixed: 'left',
      width: 220,
      render: (v: string | null | undefined, p) => (
        <span>
          {v ?? '-'}
          {p.fund_commitments__is_gp_commitment ? (
            <Tag color='purple' className='ml-1'>
              GP
            </Tag>
          ) : null}
        </span>
      )
    },
    {
      title: 'Share',
      dataIndex: 'prog__share',
      width: 150,
      align: 'right',
      render: (v: number) => <NumberText value={v} />
    },
    {
      title: 'Taken first',
      key: 'taken_first',
      width: 130,
      align: 'right',
      render: (_: unknown, p) => (hasFigure(takenFirstOf(p)) ? <NumberText value={takenFirstOf(p)} /> : null)
    },
    ...tierColumns,
    lineColumns('to_partner'),
    lineColumns('carry')
  ]

  const lineSummary = (line: (typeof totals)['to_partner'], next: () => number) => (
    <>
      <Table.Summary.Cell index={next()} align='right'>
        {line.overridden ? <NumberText value={line.calculated} wrap={struck} /> : <NumberText value={line.calculated} wrap={strong} />}
      </Table.Summary.Cell>
      <Table.Summary.Cell index={next()} align='right'>
        {hasFigure(line.correction) ? <NumberText value={line.correction} /> : null}
      </Table.Summary.Cell>
      <Table.Summary.Cell index={next()} align='right'>
        {line.overridden ? <NumberText value={line.effective} wrap={strong} /> : null}
      </Table.Summary.Cell>
      <Table.Summary.Cell index={next()} align='right'>
        <NumberText value={line.effective} wrap={strong} />
      </Table.Summary.Cell>
    </>
  )

  return (
    <Table<DistributionPartner>
      size='small'
      rowKey='fund_commitment_id'
      pagination={false}
      scroll={{ x: 'max-content' }}
      columns={columns}
      dataSource={partners}
      expandable={{
        expandedRowRender: (p) => <PartnerTierDetail partner={p} historyCheck={historyCheck} dealByDeal={dealByDeal} />,
        rowExpandable: (p) => p.tests.length > 0 || p.sources.length > 0,
        columnWidth: 48,
        fixed: 'left'
      }}
      summary={() => {
        // the expand column is leaf 0; every cell after it takes the next index
        let index = 0
        const next = () => index++
        return (
          <Table.Summary fixed>
            <Table.Summary.Row>
              <Table.Summary.Cell index={next()} />
              <Table.Summary.Cell index={next()}>
                <Text strong>Total</Text>
              </Table.Summary.Cell>
              <Table.Summary.Cell index={next()} align='right'>
                <NumberText value={totals.share} wrap={strong} />
              </Table.Summary.Cell>
              <Table.Summary.Cell index={next()} align='right'>
                {hasFigure(totals.takenFirst) ? <NumberText value={totals.takenFirst} wrap={strong} /> : null}
              </Table.Summary.Cell>
              {tierLeaves.map(({ tier, kind }, i) => (
                <Table.Summary.Cell key={`${tier.tier_sequence}-${kind}`} index={next()} align='right'>
                  <NumberText value={totals.leaves[i]} wrap={strong} />
                </Table.Summary.Cell>
              ))}
              {lineSummary(totals.to_partner, next)}
              {lineSummary(totals.carry, next)}
            </Table.Summary.Row>
          </Table.Summary>
        )
      }}
    />
  )
}
